import { useCallback, useEffect, useState, useRef } from 'react'
import { getKyousouApiClient } from '../services/network'
import { PartnerRecruitment } from '../clients/kyousou_api_client'

/**
 * 全ステータスの自社パートナー募集を取得するフック（無限スクロール対応）
 * 公開・下書き・予約投稿を全て含む
 */
export const useAllOwnRecruitment = (
  limit = 8
): {
  totalCount: number
  cursor: number
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  recruitments: PartnerRecruitment[]
  loadMore: () => void
} => {
  const apiClient = getKyousouApiClient()
  const [totalCount, setTotalCount] = useState(0)
  const [cursor, setCursor] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [recruitments, setRecruitments] = useState<PartnerRecruitment[]>([])
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasMore = cursor < totalCount

  const load = useCallback(
    async (isLoadMore = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      if (isLoadMore) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
        setCursor(0)
      }

      try {
        const response =
          await apiClient.partnerRecruitments.getPartnerRecruitments({
            page: Math.floor((isLoadMore ? cursor : 0) / limit) + 1,
            per_page: limit,
          })

        const allRecruitments = response.data.partnerRecruitments || []

        if (!abortControllerRef.current?.signal.aborted) {
          setRecruitments((prevRecruitments) => {
            if (!isLoadMore) return allRecruitments
            return [...prevRecruitments, ...allRecruitments]
          })

          setCursor((prev) =>
            isLoadMore ? prev + allRecruitments.length : allRecruitments.length
          )
          // フロントエンドで管理: 取得したアイテム数が期待値より少なければ終了
          // limitより少ない場合は最後のページと判断
          const isLastPage = allRecruitments.length < limit
          if (isLastPage) {
            setTotalCount(cursor + allRecruitments.length)
          } else {
            // まだデータがある可能性があるので大きめの値を設定
            setTotalCount(cursor + allRecruitments.length + 1)
          }
        }
      } catch (error) {
        if (!abortControllerRef.current?.signal.aborted) {
          console.error('パートナー募集の取得に失敗しました:', error)
          if (!isLoadMore) {
            setRecruitments([])
            setTotalCount(0)
            setCursor(0)
          }
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          if (isLoadMore) {
            setIsLoadingMore(false)
          } else {
            setIsLoading(false)
          }
        }
      }
    },
    [limit, cursor]
  )

  useEffect(() => {
    load(false)

    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return
    load(true)
  }, [isLoadingMore, hasMore, isLoading, load])

  return {
    totalCount,
    cursor,
    isLoading,
    isLoadingMore,
    hasMore,
    recruitments,
    loadMore,
  }
}
